import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Select, Typography } from 'antd';
import { buildOptionsFromData } from 'works/components/filtered-table';
import { Banner } from '@common/components';
import {
  ShiftTimesList,
  ShiftTimesListQueryResponse,
  ShiftTimesListQueryVariables,
} from '@scheduling/graphql';
import { useLazyQuery, useMutation, useQuery } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { CopyShiftTimesShiftsTable } from './copy-shift-times-shifts-table';
import { COPY_SHIFT_TIMES_FROM_UNIT_MUTATION } from '@scheduling/graphql/mutations/copy-shift-times';
import {
  ORGANIZATION_UNITS_QUERY,
  Unit,
  UnitsQueryResponse,
} from '@scheduling/components';

interface Props {
  unitId: string;
  setLoading: (loading: boolean) => void;
  setDisabled: (disabled: boolean) => void;
  closeDrawer: () => void;
}

export const CopyShiftTimesFromUnitSelector = forwardRef<
  { handleConfirm: () => void },
  Props
>(({ unitId, setLoading, setDisabled, closeDrawer }, ref) => {
  const [unitSelected, setUnitSelected] = useState<undefined | Unit>(undefined);

  const { data: unitsData, loading: loadingUnits } =
    useQuery<UnitsQueryResponse>(ORGANIZATION_UNITS_QUERY);

  const [getShiftTimes, { data: shiftTimes, loading: loadingShiftTimes }] =
    useLazyQuery<ShiftTimesListQueryResponse, ShiftTimesListQueryVariables>(
      ShiftTimesList,
      {
        variables: {
          input: {
            organizationUnitId: unitSelected?.id as string,
          },
        },
      }
    );

  const [copyShiftTimes, { loading: loadingCopy }] = useMutation(
    COPY_SHIFT_TIMES_FROM_UNIT_MUTATION,
    {
      refetchQueries: [
        'ShiftTimesList',
        'OrganizationUnitShiftConfigInfoQuery',
      ],
      onCompleted: () => {
        closeDrawer();

        createMessage('success', 'Shift times copied successfully.');
      },
      onError: () => {
        createMessage('error', 'Something went wrong. Please try again.');
      },
    }
  );

  useEffect(() => {
    setLoading(loadingCopy);
  }, [setLoading, loadingCopy]);

  useImperativeHandle(ref, () => ({
    handleConfirm: () => {
      if (unitSelected) {
        copyShiftTimes({
          variables: {
            input: {
              fromUnitId: unitSelected.id,
              toUnitId: unitId,
            },
          },
        });
      }
    },
  }));

  useEffect(() => {
    if (unitSelected) {
      getShiftTimes();
    }
  }, [unitSelected, getShiftTimes]);

  useEffect(() => {
    setDisabled(!unitSelected);
  }, [unitSelected, setDisabled]);

  const units = unitsData?.organizationUnits.filter(
    (unit) => unit.id !== unitId
  );

  return (
    <>
      <Typography.Text>Unit / Cost Center</Typography.Text>
      <Select
        showSearch
        id="selectFromUnit"
        style={{ width: '100%', marginBottom: 24 }}
        loading={loadingUnits}
        options={
          units &&
          buildOptionsFromData(units, (unit: Unit) => {
            {
              return {
                label: `${unit.name} - ${unit.costCenter}`,
                value: unit.id,
              };
            }
          })
        }
        filterOption={(input, option) =>
          (option?.label?.toString() ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        onSelect={(value) =>
          setUnitSelected(units?.find((unit: Unit) => unit.id === value))
        }
      />
      {unitSelected && (
        <Banner
          type="warning"
          content={`Copying shift times from a unit will override the existing shift times with ${unitSelected.name}'s shown below.`}
          style={{ marginBottom: 24 }}
        />
      )}
      <CopyShiftTimesShiftsTable
        shiftTimes={shiftTimes?.shiftTimesList.collection}
        loading={loadingShiftTimes}
        emptyText="Select a unit to review shift times to copy."
      />
    </>
  );
});
